/** Formatting helpers. All date formatting goes through the provider timezone. */

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** Seconds -> `mm:ss` (or `h:mm:ss` when >= 1h). */
export function fmtDuration(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || Number.isNaN(sec)) return '--:--';
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(r).padStart(2, '0')}`;
}

/** Seconds -> human `1h 23m` / `4m 05s`. */
export function fmtDurationHuman(sec: number | null | undefined): string {
  if (!sec) return '0s';
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(r).padStart(2, '0')}s`;
  return `${r}s`;
}

export function fmtMoney(v: number | null | undefined, digits = 4): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '$0.' + '0'.repeat(digits);
  return `$${v.toFixed(digits)}`;
}

export function fmtNumber(v: number | null | undefined): string {
  if (v === null || v === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(v);
}

export function fmtCompact(v: number | null | undefined): string {
  if (v === null || v === undefined) return '0';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
}

export function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined) return '0%';
  return `${(v * 100).toFixed(digits)}%`;
}

const dtCache = new Map<string, Intl.DateTimeFormat>();
function dtf(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = tz + JSON.stringify(opts);
  let f = dtCache.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts });
    } catch {
      f = new Intl.DateTimeFormat('en-US', opts);
    }
    dtCache.set(key, f);
  }
  return f;
}

export function fmtDate(iso: string | null | undefined, tz: string): string {
  if (!iso) return 'n/a';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'n/a';
  return dtf(tz, { year: 'numeric', month: 'short', day: '2-digit' }).format(d);
}

export function fmtDateTime(iso: string | null | undefined, tz: string): string {
  if (!iso) return 'n/a';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'n/a';
  return dtf(tz, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(d);
}

export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return 'n/a';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return 'n/a';
  const diff = (Date.now() - d) / 1000;
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (Math.abs(diff) < 60) return rtf.format(-Math.round(diff), 'second');
  if (Math.abs(diff) < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
  if (Math.abs(diff) < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
  return rtf.format(-Math.round(diff / 86400), 'day');
}

/** Sentence-case a snake_case enum value for display (`brand_safety` -> `Brand safety`). */
export function labelize(v: string | null | undefined): string {
  if (!v) return '';
  const words = v.replace(/[_.]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function truncate(s: string | null | undefined, n = 80): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/** ISO datetime string -> value for `<input type="datetime-local">`. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
